import {
  ChangeDetectorRef, Component, OnInit,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ActivatedRoute, Router,
} from '@angular/router';
import { NotificationService } from '../../../application/services/NotificationService';
import { HttpResponse } from '../../../application/types/HttpResponse';
import { ClientApiService } from '../../services/client-api/ClientApiService';
import { Client } from '../../types/Client';
import { OrderApiService } from '../../../orders/services/order-api/OrderApiService';
import {
  Order,
  OrderStatus,
} from '../../../orders/types/Order';

@Component({
  selector: 'app-client-view-page',
  standalone: false,
  templateUrl: './client-view-page.component.html',
  styleUrl: './client-view-page.component.sass',
})
export class ClientViewPageComponent implements OnInit {
  public client: Client | null = null;
  public orders: Order[] = [];
  public newNoteText = '';
  public editingNoteId: string | null = null;
  public editingNoteText = '';
  public loading = false;
  public loadingOrders = false;
  public savingNote = false;
  public errorMessage: string | null = null;

  public constructor(
    private route: ActivatedRoute,
    private router: Router,
    private clientApi: ClientApiService,
    private orderApi: OrderApiService,
    private notificationService: NotificationService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    const clientId = this.route.snapshot.paramMap.get('clientId');
    if (!clientId) {
      this.errorMessage = 'Клиент не найден.';
      return;
    }

    await this.loadClient(clientId);
  }

  public get initials(): string {
    if (!this.client) {
      return '';
    }

    return `${this.client.firstName?.charAt(0) ?? ''}${this.client.lastName?.charAt(0) ?? ''}`.toUpperCase() || 'К';
  }

  public get clientIdentifier(): string | null {
    return this.client ? this.client.publicIdentifier || this.client.id : null;
  }

  public get clientName(): string {
    if (!this.client) {
      return '';
    }

    return `${this.client.firstName ?? ''} ${this.client.lastName ?? ''}`.trim() || 'Клиент';
  }

  public get subscriptionText(): string {
    if (!this.client || this.client.teams.length === 0) {
      return 'Не выбрана';
    }

    return this.client.teams.map((team) => team.name).join(', ');
  }

  public get loyaltyBadge(): string {
    if (this.paidOrders.length >= 11) {
      return 'VIP';
    }
    if (this.paidOrders.length >= 4) {
      return 'Постоянный';
    }
    if (this.paidOrders.length >= 1) {
      return 'Новичок';
    }

    return 'Без покупок';
  }

  public get loyaltyBadgeClass(): string {
    if (this.paidOrders.length >= 11) {
      return 'vip';
    }
    if (this.paidOrders.length >= 4) {
      return 'regular';
    }
    if (this.paidOrders.length >= 1) {
      return 'rookie';
    }

    return 'empty';
  }

  public get warmthBadge(): string {
    if (this.paidOrders.length === 0) {
      return 'Новый';
    }

    const daysSinceLastPurchase = this.daysSinceLastPaidOrder;
    if (daysSinceLastPurchase !== null && daysSinceLastPurchase < 30 && this.paidOrders.length >= 3) {
      return 'Горячий';
    }
    if (daysSinceLastPurchase !== null && daysSinceLastPurchase <= 90) {
      return 'Теплый';
    }

    return 'Холодный';
  }

  public get warmthBadgeClass(): string {
    const badge = this.warmthBadge;
    if (badge === 'Горячий') {
      return 'hot';
    }
    if (badge === 'Теплый') {
      return 'warm';
    }
    if (badge === 'Холодный') {
      return 'cold';
    }

    return 'new';
  }

  public get hasReservedOrders(): boolean {
    return this.orders.some((order) => order.status === 'reserved');
  }

  public get paidOrders(): Order[] {
    return this.orders.filter((order) => order.status === 'paid');
  }

  public get lastPaidOrderDate(): Date | null {
    const paidTimestamps = this.paidOrders
      .map((order) => new Date(order.paidAt ?? order.createdAt).getTime())
      .filter((timestamp) => Number.isFinite(timestamp));
    if (paidTimestamps.length === 0) {
      return null;
    }

    return new Date(Math.max(...paidTimestamps));
  }

  public get daysSinceLastPaidOrder(): number | null {
    if (!this.lastPaidOrderDate) {
      return null;
    }

    const dayMs = 24 * 60 * 60 * 1000;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const purchaseDate = new Date(this.lastPaidOrderDate);
    purchaseDate.setHours(0, 0, 0, 0);

    return Math.max(Math.floor((today.getTime() - purchaseDate.getTime()) / dayMs), 0);
  }

  public get paidOrdersTotal(): number {
    return this.paidOrders.reduce((sum, order) => sum + order.total, 0);
  }

  public get paidTicketsTotal(): number {
    return this.paidOrders.reduce((sum, order) => sum + order.items.length, 0);
  }

  public get averagePaidOrderTotal(): number {
    return this.paidOrders.length > 0 ? this.paidOrdersTotal / this.paidOrders.length : 0;
  }

  public get paidOrderSparkBars(): number[] {
    const paidTotals = [
      ...this.paidOrders,
    ]
      .sort((firstOrder, secondOrder) => new Date(firstOrder.paidAt ?? firstOrder.createdAt).getTime() - new Date(secondOrder.paidAt ?? secondOrder.createdAt).getTime())
      .slice(-6)
      .map((order) => order.total);
    if (paidTotals.length === 0) {
      return [
        16,
        16,
        16,
      ];
    }

    const maxTotal = Math.max(...paidTotals, 1);
    return paidTotals.map((total) => Math.max(Math.round((total / maxTotal) * 100), 18));
  }

  public async deleteClient(): Promise<void> {
    if (!this.client || !window.confirm(`Удалить клиента ${this.client.firstName ?? ''} ${this.client.lastName ?? ''}`.trim())) {
      return;
    }

    try {
      const response = await this.clientApi.deleteClient(this.clientIdentifier ?? this.client.id);
      if (response.status === 'failed') {
        this.errorMessage = response.errorMessage;
        return;
      }

      this.notificationService.notify(
        'client',
        'Клиент удален',
        `${this.client.firstName ?? ''} ${this.client.lastName ?? ''}`.trim() || 'Клиент',
      );
      await this.router.navigate([
        '/dashboard/clients',
      ]);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.changeDetector.detectChanges();
    }
  }

  public async addNote(): Promise<void> {
    if (!this.client || !this.newNoteText.trim()) {
      return;
    }

    this.savingNote = true;
    this.errorMessage = null;

    try {
      const response = await this.clientApi.addClientNote(this.clientIdentifier ?? this.client.id, this.newNoteText.trim());
      const client = this.unwrapResponse(response);
      if (client) {
        this.client = client;
        this.newNoteText = '';
        this.notificationService.notify(
          'client',
          'Добавлена заметка',
          `${client.firstName ?? ''} ${client.lastName ?? ''}`.trim() || 'Клиент',
        );
      }
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.savingNote = false;
      this.changeDetector.detectChanges();
    }
  }

  public startEditNote(noteId: string, text: string): void {
    this.editingNoteId = noteId;
    this.editingNoteText = text;
  }

  public cancelEditNote(): void {
    this.editingNoteId = null;
    this.editingNoteText = '';
  }

  public async saveNote(noteId: string): Promise<void> {
    if (!this.client || !this.editingNoteText.trim()) {
      return;
    }
    try {
      const response = await this.clientApi.updateClientNote(this.clientIdentifier ?? this.client.id, noteId, this.editingNoteText);
      const client = this.unwrapResponse(response);
      if (client) {
        this.client = client;
        this.cancelEditNote();
      }
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.changeDetector.detectChanges();
    }
  }

  public async deleteNote(noteId: string): Promise<void> {
    if (!this.client || !window.confirm('Удалить заметку?')) {
      return;
    }
    try {
      const response = await this.clientApi.deleteClientNote(this.clientIdentifier ?? this.client.id, noteId);
      const client = this.unwrapResponse(response);
      if (client) {
        this.client = client;
      }
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.changeDetector.detectChanges();
    }
  }

  public formatDate(value: string): string {
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value));
  }

  public formatMoney(value: number): string {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      maximumFractionDigits: 0,
    }).format(value);
  }

  public getOrderStatusName(status: OrderStatus): string {
    const names: Record<OrderStatus, string> = {
      cancelled: 'Отменен',
      draft: 'Черновик',
      expired: 'Резерв истек',
      paid: 'Оплачен',
      refunded: 'Возврат',
      reserved: 'Резерв',
    };

    return names[status];
  }

  public getOrderStatusClass(status: OrderStatus): string {
    if (status === 'paid') {
      return 'paid';
    }
    if (status === 'reserved') {
      return 'reserved';
    }
    if (status === 'cancelled' || status === 'expired') {
      return 'cancelled';
    }

    return 'muted';
  }

  public getOrderTicketsText(order: Order): string {
    return order.items.map((item) => item.ticket?.code || '—').join(', ') || '—';
  }

  public formatPhone(value: string | null): string {
    const digits = value?.replace(/\D/g, '') ?? '';
    if (digits.length !== 11 || !digits.startsWith('7')) {
      return value ?? 'Не указан';
    }

    return `+7 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9, 11)}`;
  }

  public formatGroupedNumber(value: string | null): string {
    const digits = value?.replace(/\D/g, '') ?? '';
    return digits ? digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : 'Не указан';
  }

  public formatDiscount(value: number | null): string {
    return value === null ? 'Не указана' : `${value}%`;
  }

  public trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  private async loadClient(clientId: string): Promise<void> {
    this.loading = true;
    this.errorMessage = null;

    try {
      const response = await this.clientApi.getClient(clientId);
      const client = this.unwrapResponse(response);
      if (!client) {
        this.errorMessage = 'Клиент не найден.';
        return;
      }

      this.client = client;
      await this.loadClientOrders(client.id);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  private async loadClientOrders(clientId: string): Promise<void> {
    this.loadingOrders = true;
    try {
      const response = await this.orderApi.getOrders({ clientId });
      const orders = this.unwrapResponse(response);
      this.orders = orders ?? [];
    } catch (error) {
      this.handleUnknownError(error);
      this.orders = [];
    } finally {
      this.loadingOrders = false;
    }
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }

    return response.response;
  }

  private handleUnknownError(error: unknown): void {
    const apiErrorMessage = error instanceof HttpErrorResponse
      ? this.extractApiErrorMessage(error)
      : null;
    this.errorMessage = apiErrorMessage ?? (error instanceof Error ? error.message : 'Не удалось выполнить действие.');
  }

  private extractApiErrorMessage(error: HttpErrorResponse): string | null {
    if (typeof error.error?.errorMessage === 'string') {
      return error.error.errorMessage;
    }

    if (typeof error.error?.message === 'string') {
      return error.error.message;
    }

    return null;
  }
}
